import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

/**
 * Compact Outcomes tile on the Service User profile.
 *
 * Renders the person-scoped summary (`GET /outcomes/service-user/{id}/summary`)
 * plus up to 5 recent outcomes, deep-linking to `/outcomes/{id}`. Collapsed by
 * default so the primary Overview/About Me/Needs cards appear higher on the
 * page; expanding reveals the top-5 outcomes list.
 */
export default function ClientOutcomesPanel({ clientId }) {
  const [state, setState] = useState({ loading: true });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/outcomes/service-user/${clientId}/summary`);
        if (!alive) return;
        setState({ loading: false, summary: r.data.summary, top: r.data.top || [] });
      } catch {
        if (alive) setState({ loading: false, summary: null, top: [] });
      }
    })();
    return () => { alive = false; };
  }, [clientId]);

  if (state.loading) return null;
  const s = state.summary || {};
  const chips = [
    { k: "active", label: "Active", value: s.active || 0 },
    { k: "on_track", label: "On Track", value: s.on_track || 0, tone: "emerald" },
    { k: "at_risk", label: "At Risk", value: s.at_risk || 0, tone: "amber" },
    { k: "achieved", label: "Achieved", value: s.achieved || 0, tone: "emerald" },
    { k: "reviews_due", label: "Reviews Due", value: s.reviews_due || 0, tone: "sky" },
  ];

  return (
    <div className="px-6 pt-2" data-testid="client-outcomes-panel">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="bg-white border border-slate-200 rounded-md">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 flex-wrap hover:bg-slate-50 transition-colors"
              data-testid="client-outcomes-toggle"
              aria-expanded={open}
            >
              <div className="flex items-center gap-2 shrink-0">
                <Target className="w-4 h-4 text-emerald-600" />
                <div className="font-heading text-sm font-semibold text-slate-900">Outcomes</div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="client-outcomes-chips">
                {chips.map((c) => (
                  <span key={c.k} className={`text-[11px] px-2 py-0.5 rounded-full border tabular-nums ${
                    c.tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-800" :
                    c.tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-800" :
                    c.tone === "sky" ? "border-sky-200 bg-sky-50 text-sky-800" :
                    "border-slate-200 bg-slate-50 text-slate-700"
                  }`} data-testid={`client-outcomes-${c.k}`}>{c.label}: {c.value}</span>
                ))}
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-4 pb-4 pt-1 border-t border-slate-100">
              <div className="flex items-center justify-end mb-1.5">
                <Link to={`/outcomes?service_user_id=${clientId}`} className="text-[12px] text-emerald-700 hover:underline" data-testid="client-outcomes-view-all">View all</Link>
              </div>
              {state.top && state.top.length > 0 ? (
                <ul className="divide-y divide-slate-100" data-testid="client-outcomes-top">
                  {state.top.map((o) => (
                    <li key={o.id} className="py-1.5 flex items-center justify-between gap-3">
                      <Link to={`/outcomes/${o.id}`} className="min-w-0 flex-1 text-sm text-slate-800 hover:text-emerald-700 truncate" data-testid={`client-outcome-${o.id}`}>
                        {o.title}
                      </Link>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">{(o.status || "").replace("_", " ")}</span>
                      {typeof o.progress_value === "number" && <span className="text-[11px] tabular-nums text-emerald-700">{o.progress_value}%</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-[12px] text-slate-500">No outcomes recorded yet for this person.</div>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
